import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ArrowRight, Clock } from "lucide-react";
import { fmtDate } from "@/components/ArticleBody";

export const ArticleCard = ({ a, i = 0 }) => (
  <motion.article initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
    data-testid={`article-card-${a.slug}`} className="group bg-white border border-gray-200 flex flex-col hover:border-[#E0C173] hover:-translate-y-1 transition-[transform,border-color] duration-300">
    <Link to={`/conseils/${a.slug}`} className="block aspect-[16/10] overflow-hidden bg-[#F8F6F0]">
      {a.image && <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
    </Link>
    <div className="p-6 flex flex-col flex-1">
      <div className="text-[11px] uppercase tracking-widest text-[#C9A85A]">{a.category}</div>
      {a.sector && <div className="mt-1 text-[11px] uppercase tracking-widest text-[#94A3B8] truncate">{a.sector}</div>}
      <h3 className="mt-3 font-display text-xl text-[#1B2A4A] leading-snug"><Link to={`/conseils/${a.slug}`} className="hover:text-[#C9A85A] transition-colors">{a.title}</Link></h3>
      <p className="mt-3 text-sm text-[#475569] leading-relaxed flex-1">{a.excerpt}</p>
      <div className="mt-5 flex items-center justify-between text-xs text-[#94A3B8]">
        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {fmtDate(a.published_at)}</span>
        <Link to={`/conseils/${a.slug}`} data-testid={`article-read-${a.slug}`} className="inline-flex items-center gap-1 text-[#1B2A4A] font-medium hover:text-[#C9A85A]">Lire <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></Link>
      </div>
    </div>
  </motion.article>
);

export default function ArticlesSection() {
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState("Tous");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { api.get("/articles").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (items.length && window.location.hash === "#articles") document.getElementById("articles")?.scrollIntoView(); }, [items]);
  if (!items.length) return null;
  const groupOf = (a) => (a.department ? "Par département" : a.category || "Conseils");
  const ORDER = ["Tous", "Cas de figure", "Reprendre une entreprise", "Par département"];
  const found = [...new Set(items.map(groupOf))];
  const groups = ["Tous", ...ORDER.filter((g) => found.includes(g)), ...found.filter((g) => !ORDER.includes(g))];
  const filtered = group === "Tous" ? items : items.filter((a) => groupOf(a) === group);
  const shown = showAll ? filtered : filtered.slice(0, 8);
  return (
    <section className="bg-[#F8F6F0] border-y border-[#E0C173]/30" data-testid="articles-section" id="articles">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="max-w-2xl">
          <span className="text-[#E0C173] uppercase tracking-[0.25em] text-xs font-medium">Le blog PROAC</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#1B2A4A] tracking-tight">Cas de figure & articles</h2>
          <p className="mt-4 text-[#475569]">Des situations concrètes de cession, secteur par secteur, pour vous aider à préparer la vôtre.</p>
        </div>
        {groups.length > 2 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="articles-filters">
            {groups.map((g) => (
              <button key={g} type="button" onClick={() => { setGroup(g); setShowAll(false); }} data-testid={`articles-filter-${g}`}
                className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${group === g ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-[#1B2A4A] border-gray-300 hover:border-[#1B2A4A]"}`}>{g}</button>
            ))}
          </div>
        )}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {shown.map((a, i) => <ArticleCard key={a.slug} a={a} i={i % 4} />)}
        </div>
        {filtered.length > shown.length && (
          <div className="mt-10 text-center"><button type="button" onClick={() => setShowAll(true)} data-testid="articles-show-more" className="rounded-full border border-[#1B2A4A] text-[#1B2A4A] px-6 py-2 text-sm hover:bg-[#1B2A4A] hover:text-white transition-colors">Voir tous les articles ({filtered.length})</button></div>
        )}
      </div>
    </section>
  );
}

